import React, { MouseEventHandler, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/* MUI */
import { Theme } from '@mui/material/styles';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import List from '@mui/material/List';
import ListSubheader from '@mui/material/ListSubheader';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Skeleton from '@mui/material/Skeleton';
import { SvgIconTypeMap } from '@mui/material/SvgIcon';

/* Icons */
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReportIcon from '@mui/icons-material/Report';
import WarningIcon from '@mui/icons-material/Warning';
import CloudIcon from '@mui/icons-material/Cloud';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';

/* Local */
import { MyProperty } from '../property-detail';
import {
  convertToTempUnits,
  formatTemperatureString,
} from '../../devices/util';
import {
  PropertySummaryAlertsQuery,
  PropertySummaryDevicesQuery,
  PropertySummaryStatsQuery,
  PropertySummaryUnitsQuery,
  Property,
  useCheckOpenWeatherDevicesQuery,
  useInitializeLocalWeatherDeviceMutation,
  usePropertySummaryAlertsQuery,
  usePropertySummaryDevicesQuery,
  usePropertySummaryStatsQuery,
  usePropertySummaryUnitsQuery,
} from '../../../../types/generated-types';
import { Notifier } from '../../../system/services/notificationManager';
import { useAuthenticator } from '../../../auth/AuthenticationContext';
import { Actions } from '../../../auth/types/ability';
import {
  BottomMenuItems,
  useInjectableComponents,
} from '../../../system/services/injectableComponentsManager';
import { basePropertyMenuItems } from '../ui/base-property-context-menu';
import BorderedSection from '../../shared/borderedSection';
import FullWidthLoadingSkeleton from '../../shared/fullWidthLoadingSkeleton';
import { useIsLargeScreen } from '../../../system/SystemMediaQueriesProvider';

type IconColor = SvgIconTypeMap['props']['color'];
type PropertySummaryAlertsData = NonNullable<
  PropertySummaryAlertsQuery['propertySummaryAlerts']
>;
type PropertySummaryDevicesData = NonNullable<
  PropertySummaryDevicesQuery['propertySummaryDevices']
>;
type PropertySummaryStatsData = NonNullable<
  PropertySummaryStatsQuery['propertySummaryStats']
>;
type PropertySummaryUnitsData = NonNullable<
  PropertySummaryUnitsQuery['propertySummaryUnits']
>;

const PROPERTY_SUMMARY_POLL_INTERVAL_MS = 60000;

interface PropertySummaryProps {
  // TODO: Peter: Unused (theme). Do we need to have this here?
  theme: Theme;
  data?: MyProperty;
}

// /**
//  * Custom hook to handle polling cleanup when component unmounts
//  * This ensures that polling stops when navigating away from the page
//  */
// function usePollingQuery(queryHook: (options: any) => any, options: any) {
//   const result = queryHook(options);
//
//   useEffect(() => {
//     // Return cleanup function that stops polling when component unmounts
//     return () => {
//       console.log('clearing polling for operation:', result.operationName);
//
//       // Stop polling for this specific query instance
//       if (result.stopPolling) {
//         result.stopPolling();
//       }
//     };
//   }, [result]);
//
//   return result;
// }

/**
 * General property-wide overview and statistics
 */
export function PropertySummary(props: PropertySummaryProps) {
  const { data } = props;
  const isLarge = useIsLargeScreen();
  const {
    setContextMenuItems,
    setSubtitle,
    setTitle,
    setTitlePath,
    setSubtitlePath,
  } = useInjectableComponents();
  const { pathname } = useLocation();
  const { user } = useAuthenticator();
  const propertyId = data?._id || '';
  const isSuperUser = user?.isSuper || false;

  const { data: unitsData, loading: unitsLoading } =
    usePropertySummaryUnitsQuery({
      variables: { propertyId },
      skip: !propertyId,
      fetchPolicy: 'cache-and-network',
      pollInterval: PROPERTY_SUMMARY_POLL_INTERVAL_MS,
      onError(error) {
        console.error(error);
        Notifier.error('Error loading property unit summary info');
      },
    });
  const { data: alertsData, loading: alertsLoading } =
    usePropertySummaryAlertsQuery({
      variables: { propertyId },
      skip: !propertyId,
      fetchPolicy: 'cache-and-network',
      pollInterval: PROPERTY_SUMMARY_POLL_INTERVAL_MS,
      onError(error) {
        console.error(error);
        Notifier.error('Error loading property alert summary info');
      },
    });
  const { data: devicesData, loading: devicesLoading } =
    usePropertySummaryDevicesQuery({
      variables: { propertyId },
      skip: !propertyId,
      fetchPolicy: 'cache-and-network',
      pollInterval: PROPERTY_SUMMARY_POLL_INTERVAL_MS,
      onError(error) {
        console.error(error);
        Notifier.error('Error loading property device summary info');
      },
    });
  const { data: statsData, loading: statsLoading } =
    usePropertySummaryStatsQuery({
      variables: { propertyId },
      skip: !propertyId,
      fetchPolicy: 'cache-and-network',
      pollInterval: PROPERTY_SUMMARY_POLL_INTERVAL_MS,
      onError(error) {
        console.error(error);
        Notifier.error('Error loading property statistics summary info');
      },
    });

  const units = unitsData?.propertySummaryUnits ?? undefined;
  const alerts = alertsData?.propertySummaryAlerts ?? undefined;
  const devices = devicesData?.propertySummaryDevices ?? undefined;
  const stats = statsData?.propertySummaryStats ?? undefined;

  // Check if OpenWeather devices exist for this property
  const { data: openWeatherData } = useCheckOpenWeatherDevicesQuery({
    variables: { propertyId },
    skip: !propertyId || !isSuperUser,
    fetchPolicy: 'cache-and-network',
  });

  const hasOpenWeatherDevice =
    openWeatherData?.devices && openWeatherData.devices.length > 0;

  // Mutation hook for initializing local weather device
  const [initializeLocalWeatherDevice, { loading: isInitializing }] =
    useInitializeLocalWeatherDeviceMutation({
      onCompleted: () => {
        Notifier.success('Local weather device initialized successfully');
      },
      onError: (error: Error) => {
        console.error('Error initializing local weather device:', error);
        Notifier.error(
          error.message || 'Failed to initialize local weather device',
        );
      },
      refetchQueries: ['CheckOpenWeatherDevices'],
    });

  const handleAddLocalWeather = useCallback(async () => {
    if (!propertyId) return;

    try {
      await initializeLocalWeatherDevice({
        variables: {
          propertyId,
        },
      });
    } catch (error) {
      // Error is handled by onError callback
      console.error('Error in handleAddLocalWeather:', error);
    }
  }, [propertyId, initializeLocalWeatherDevice]);

  useEffect(() => {
    if (data?._id) {
      const contextMenuItems: BottomMenuItems = [
        ...basePropertyMenuItems(data._id, pathname),
      ];

      if (isSuperUser) {
        contextMenuItems.push({
          label: 'Reports',
          showDivider: true,
          items: [
            {
              id: 'offline-device-report-menu-item',
              label: 'Offline Device Report',
              icon: <SummarizeOutlinedIcon fontSize="small" />,
              navTarget: `/properties/${data._id}/reports/device-offline`,
              permit: {
                action: 'view' as Actions,
                subject: data as Property,
              },
            },
          ],
        });
      }

      // Add "Add Local Weather" menu item if no OpenWeather device exists and user is super
      if (!hasOpenWeatherDevice && !isInitializing && isSuperUser) {
        const propertyActionsGroup = contextMenuItems.find(
          (group) => group.label === 'Property Actions',
        );

        const addLocalWeatherItem = {
          id: 'add-local-weather-menu-item',
          label: 'Add Local Weather',
          icon: <CloudIcon fontSize="small" />,
          action: handleAddLocalWeather,
          permit: {
            action: 'update' as Actions,
            subject: data as Property,
          },
        };

        if (propertyActionsGroup) {
          propertyActionsGroup.items.push(addLocalWeatherItem);
        } else {
          contextMenuItems.push({
            label: 'Property Actions',
            items: [addLocalWeatherItem],
          });
        }
      }

      setContextMenuItems(contextMenuItems, data as Property);
    }

    return () => {
      setContextMenuItems(undefined);
    };
  }, [
    pathname,
    data,
    setContextMenuItems,
    hasOpenWeatherDevice,
    isInitializing,
    handleAddLocalWeather,
    isSuperUser,
  ]);

  useEffect(() => {
    if (data?._id) {
      setTitle(data.title ?? 'Unnamed Property');
      setTitlePath(`/properties`);

      setSubtitle('Property Summary');
      setSubtitlePath(`/properties/${data?._id}/units`);
    }
    return () => {
      setTitle(undefined);
      setTitlePath(undefined);
      setSubtitle(undefined);
      setSubtitlePath(undefined);
    };
  }, [data, setSubtitle, setSubtitlePath, setTitle, setTitlePath]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: isLarge ? 1 : 'unset',
        alignItems: 'stretch',
        backgroundColor: '#edfcf4',
        height: '100%',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: isLarge ? 'row' : 'column',
          flexWrap: isLarge ? 'wrap' : 'unset',
          flex: 1,
          gap: '10px',
          justifyContent: 'start',
          padding: isLarge ? '10px 6px 6px 6px' : '0px 6px',
        }}
      >
        <div
          style={{
            display: 'flex',
            flex: isLarge ? '1 1 auto' : 'unset',
            flexDirection: isLarge ? 'column' : 'row',
          }}
        >
          <UnitsSummary
            {...props}
            isLarge={isLarge}
            units={units}
            loading={unitsLoading && !units}
          />
        </div>
        <div
          style={{
            display: 'flex',
            flex: isLarge ? '1 1 auto' : 'unset',
            flexDirection: isLarge ? 'column' : 'row',
          }}
        >
          <AlertSummary
            {...props}
            isLarge={isLarge}
            alerts={alerts}
            loading={alertsLoading && !alerts}
          />
        </div>
        <div
          style={{
            display: 'flex',
            flex: isLarge ? '1 1 auto' : 'unset',
            flexDirection: isLarge ? 'column' : 'row',
          }}
        >
          <PropertyStats
            {...props}
            isLarge={isLarge}
            devices={devices}
            stats={stats}
            loading={(devicesLoading && !devices) || (statsLoading && !stats)}
          />
        </div>
      </div>
    </div>
  );
}

export interface PropertySummarySectionProps extends PropertySummaryProps {
  isLarge: boolean;
  loading: boolean;
}

interface UnitsSummaryProps extends PropertySummarySectionProps {
  units?: PropertySummaryUnitsData;
}

function UnitsSummary(props: UnitsSummaryProps) {
  const { isLarge, loading, units } = props;
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const residential = units?.units[0];
  const commonArea = units?.units[1];
  const centralEquipment = units?.units[2];

  return loading ? (
    <FullWidthLoadingSkeleton />
  ) : (
    <Card
      sx={{
        backgroundColor: '#edfcf4',
        display: 'flex',
        flexDirection: 'column',
        flex: isLarge ? 'unset' : 1,
        margin: isLarge ? '0px 0px 0px 0px' : '4px 0px 0px 0px',
        '& .MuiCardActions-root': { margin: '0px', padding: '4px' },
      }}
    >
      <CardActions
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'stretch',
        }}
      >
        <BorderedSection
          title="View Filtered Unit List"
          style={
            isLarge
              ? { backgroundColor: 'white', marginTop: '4px' }
              : {
                  margin: '4px 0px 0px 0px',
                  padding: '0px',
                  backgroundColor: 'white',
                }
          }
          ccStyle={
            isLarge
              ? {
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-evenly',
                  padding: '4px 0px 16px',
                }
              : {
                  margin: '-2px 0px 4px 0px',
                  padding: '0px',
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-evenly',
                }
          }
        >
          <Button
            onClick={() =>
              navigate(
                pathname.replace('summary', 'units?unitType=residential'),
              )
            }
            color="secondary"
            //fullWidth
            variant="contained"
            disabled={(residential?.count ?? 0) === 0}
            aria-label="residential-units"
            sx={{ color: '#fff' }}
          >
            Residential
          </Button>
          <Button
            onClick={() =>
              navigate(pathname.replace('summary', 'units?unitType=commonArea'))
            }
            color="secondary"
            //fullWidth
            variant="contained"
            disabled={(commonArea?.count ?? 0) === 0}
            aria-label="common-units"
            sx={{ color: '#fff' }}
          >
            Common
          </Button>
          <Button
            onClick={() =>
              navigate(
                pathname.replace('summary', 'units?unitType=centralEquipment'),
              )
            }
            color="secondary"
            //fullWidth
            variant="contained"
            disabled={(centralEquipment?.count ?? 0) === 0}
            aria-label="central-units"
            sx={{ color: '#fff' }}
          >
            Central
          </Button>
        </BorderedSection>
      </CardActions>
    </Card>
  );
}

/* Alerts Summary Card */
interface AlertSummaryProps extends PropertySummarySectionProps {
  alerts?: PropertySummaryAlertsData;
}

function AlertSummary(props: AlertSummaryProps) {
  const { isLarge, loading, alerts } = props;
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const navToAlerts: MouseEventHandler<HTMLButtonElement> = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      navigate(pathname.replace('summary', 'alerts'));
    },
    [pathname, navigate],
  );

  const alertsCount = alerts?.activeAlertCount ?? 0;
  const hasAlerts = alertsCount > 0;
  const sections = [
    {
      title: 'Offline Status',
      items:
        alerts?.statusItems.filter((item) => item.group === 'status') ?? [],
    },
    {
      title: `Alerts — ${alertsCount} active`,
      items: alerts?.statusItems.filter((item) => item.group === 'alert') ?? [],
    },
    {
      title: 'Installation Status',
      items:
        alerts?.statusItems.filter((item) => item.group === 'installation') ??
        [],
    },
  ];

  return (
    <Card
      sx={{
        backgroundColor: '#edfcf4',
        display: 'flex',
        flexDirection: 'column',
        flex: isLarge ? 'unset' : 1,
        '& .MuiCardContent-root': {
          border: 'none',
          padding: '0px !important',
          margin: isLarge ? '10px 0px 0px 0px' : '2px 0px 0px 0px',
        },
      }}
    >
      <CardContent>
        <BorderedSection
          style={{ margin: '4px', backgroundColor: 'white' }}
          ccStyle={{ marginTop: '-6px', paddingBottom: '0px' }}
          title="Property Status"
        >
          {loading && (
            <>
              <Skeleton variant="text" />
              <Skeleton variant="text" />
              <Skeleton variant="text" />
            </>
          )}
          {sections.map((section) => (
            <React.Fragment key={section.title}>
              <div
                style={{
                  fontSize: '1em',
                  color: '#4a776e',
                  fontWeight: 'bold',
                  lineHeight: '30px',
                  marginLeft: '10px',
                  width: 'fit-content',
                }}
              >
                {section.title}
              </div>
              <List
                sx={{
                  width: 'auto',
                  maxWidth: 500,
                  paddingTop: 0,
                  marginLeft: isLarge ? 2 : 1,
                  marginRight: isLarge ? 2 : 1,
                }}
                aria-label={section.title}
              >
                {section.items.map((item) => (
                  <ListItem
                    key={item.label}
                    sx={{ height: '20px', padding: 0 }}
                  >
                    <ListItemIcon
                      style={{ minWidth: '24px', marginRight: '10px' }}
                    >
                      {item.color === 'success' ? (
                        <CheckCircleIcon color="success" />
                      ) : item.color === 'error' ? (
                        <ReportIcon color="error" />
                      ) : (
                        <WarningIcon
                          color={(item.color as IconColor) || 'warning'}
                        />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      style={{
                        flex: '1 1 auto',
                        minWidth: 0,
                        margin: 0,
                        textDecoration: 'none',
                        fontWeight: item.count > 0 ? '800' : 'normal',
                        cursor: item.action ? 'pointer' : 'default',
                      }}
                      primaryTypographyProps={{
                        fontSize: '1em',
                        fontWeight: 'bolder',
                      }}
                      onClick={() => {
                        if (item.action) {
                          const targetPath = pathname.replace(
                            'summary',
                            item.action,
                          );
                          navigate(targetPath, { state: item.options });
                        }
                      }}
                    >
                      {item.label}:
                    </ListItemText>
                    <ListItemText
                      style={{
                        flex: '0 0 auto',
                        fontWeight: 'normal',
                        textAlign: 'right',
                      }}
                      secondaryTypographyProps={{ fontWeight: 'normal' }}
                    >
                      {item.total === null || item.total === undefined
                        ? item.count
                        : `${item.count} of ${item.total}`}
                    </ListItemText>
                  </ListItem>
                ))}
              </List>
            </React.Fragment>
          ))}

          {/*</Collapse>*/}
          {hasAlerts && (
            <CardActions
              sx={{
                p: 2,
                pt: isLarge ? 1 : 0,
                pb: 0,
                mb: isLarge ? '12px' : '6px',
                mt: 0,
              }}
            >
              <Button
                onClick={navToAlerts}
                fullWidth
                color="secondary"
                variant="contained"
                sx={{ color: '#fff' }}
              >
                View alerts
              </Button>
            </CardActions>
          )}
        </BorderedSection>
      </CardContent>
    </Card>
  );
}

/* Property Statistics Card */
interface PropertyStatsProps extends PropertySummarySectionProps {
  devices?: PropertySummaryDevicesData;
  stats?: PropertySummaryStatsData;
}

function PropertyStats(props: PropertyStatsProps) {
  const { isLarge, loading, devices, stats } = props;
  const { user } = useAuthenticator();

  const { residentialDevices, centralDevices } = devices || {};
  const hasResidentialDevices =
    residentialDevices && residentialDevices.length > 0;
  const hasCentralDevices = centralDevices && centralDevices.length > 0;

  const { temperature, humidity } = stats || {};
  const showStats = temperature || humidity;

  function formatTemp(tempInC: number) {
    const displayUnits = user?.preferredUnits || 'F';
    const temp = convertToTempUnits(tempInC, displayUnits, 'C', 0) ?? 0;
    return formatTemperatureString(temp, displayUnits);
  }

  function formatHumidity(humidity: number) {
    return Math.round(humidity) + '%';
  }

  return (
    <Card
      sx={{
        backgroundColor: '#edfcf4',
        display: 'flex',
        flexDirection: 'column',
        flex: isLarge ? 'unset' : 1,
        marginBottom: '30px',
        '& .MuiCardContent-root': {
          border: 'none',
          padding: '0px ',
          margin: isLarge ? '10px 0px 0px 0px' : '2px 0px 0px 0px',
        },
      }}
    >
      <CardContent>
        <BorderedSection
          style={{ margin: '2px', backgroundColor: 'white' }}
          ccStyle={{
            paddingTop: isLarge ? '4px' : '0px',
            paddingBottom: isLarge ? '16px' : '4px',
          }}
          title={'Summary Statistics'}
        >
          {loading && (
            <>
              <Skeleton variant="text" />
              <Skeleton variant="text" />
              <Skeleton variant="text" />
            </>
          )}
          {hasResidentialDevices && (
            <>
              <List
                sx={{
                  width: '100%',
                  maxWidth: 500,
                  paddingTop: 0,
                  marginLeft: isLarge ? 2 : 1,
                }}
                aria-labelledby="Central Equipment Stats"
              >
                <ListSubheader
                  disableGutters={true}
                  sx={{
                    fontSize: '1em',
                    color: '#4a776e',
                    fontWeight: 'bold',
                    lineHeight: '30px',
                    width: 'fit-content',
                  }}
                >
                  Residential and Common Area Devices
                </ListSubheader>
                {residentialDevices.map((device) => {
                  return (
                    <ListItem
                      key={device.label}
                      sx={{
                        height: '24px',
                      }}
                    >
                      <ListItemText
                        style={{
                          width: isLarge ? '60%' : '85%',
                          margin: 0,
                        }}
                        primaryTypographyProps={{
                          fontSize: '1em',
                          fontWeight: 'bolder',
                        }}
                      >
                        {device.label}:
                      </ListItemText>
                      <ListItemText
                        style={{
                          width: isLarge ? '40%' : '15%',
                          fontWeight: 'normal',
                        }}
                        secondaryTypographyProps={{ fontWeight: 'normal' }}
                      >
                        {device.count}
                      </ListItemText>
                    </ListItem>
                  );
                })}
              </List>
            </>
          )}
          {hasCentralDevices && (
            <>
              <List
                sx={{
                  width: '100%',
                  maxWidth: 400,
                  marginLeft: 2,
                }}
                aria-labelledby="Central Equipment Stats"
              >
                <ListSubheader
                  disableGutters={true}
                  sx={{
                    fontSize: '1em',
                    color: '#4a776e',
                    fontWeight: 'bold',
                    lineHeight: '30px',
                    width: 'fit-content',
                  }}
                >
                  Central Equipment
                </ListSubheader>
                {centralDevices.map((device) => {
                  return (
                    <ListItem
                      key={device.label}
                      sx={{
                        height: '24px',
                      }}
                    >
                      <ListItemText
                        style={{
                          width: isLarge ? '60%' : '70%',
                          margin: 0,
                        }}
                        primaryTypographyProps={{
                          fontSize: '1em',
                          fontWeight: 'bolder',
                        }}
                      >
                        {device.label}:
                      </ListItemText>
                      <ListItemText
                        style={{
                          width: isLarge ? '40%' : '30%',
                          fontWeight: 'normal',
                        }}
                        secondaryTypographyProps={{ fontWeight: 'normal' }}
                      >
                        {device.count}
                      </ListItemText>
                    </ListItem>
                  );
                })}
              </List>
            </>
          )}
          {showStats && (
            <>
              {isLarge ? <Divider sx={{ mb: isLarge ? 2 : 1, mt: 1 }} /> : null}
              {temperature && (
                <>
                  <List
                    sx={{
                      width: '100%',
                      maxWidth: 400,
                      marginLeft: isLarge ? 2 : 1,
                    }}
                    aria-labelledby="Temperature Stats"
                  >
                    <ListSubheader
                      disableGutters={true}
                      sx={{
                        fontSize: '1em',
                        color: '#4a776e',
                        fontWeight: 'bold',
                        lineHeight: '30px',
                        width: 'fit-content',
                      }}
                    >
                      Temperature
                    </ListSubheader>
                    <ListItem
                      sx={{
                        height: '24px',
                      }}
                    >
                      <ListItemText
                        style={{
                          width: isLarge ? '40%' : '20%',
                          margin: 0,
                        }}
                        primaryTypographyProps={{
                          fontSize: '1em',
                          fontWeight: 'bolder',
                        }}
                      >
                        {`${isLarge ? 'Average' : 'Avg'}:`}
                      </ListItemText>
                      <ListItemText
                        style={{
                          width: '30%',
                          fontWeight: 'normal',
                        }}
                        secondaryTypographyProps={{ fontWeight: 'normal' }}
                      >
                        {`${formatTemp(temperature.ave)}`}
                      </ListItemText>
                      <ListItemText
                        style={{
                          width: isLarge ? '25%' : '20%',
                          margin: 0,
                        }}
                        primaryTypographyProps={{
                          fontSize: '1em',
                          fontWeight: 'bolder',
                        }}
                      >
                        Min:
                      </ListItemText>
                      <ListItemText
                        style={{
                          width: '30%',
                          fontWeight: 'normal',
                        }}
                        secondaryTypographyProps={{ fontWeight: 'normal' }}
                      >
                        {formatTemp(temperature.min)}{' '}
                      </ListItemText>
                      <ListItemText
                        style={{
                          width: isLarge ? '25%' : '20%',
                          margin: 0,
                        }}
                        primaryTypographyProps={{
                          fontSize: '1em',
                          fontWeight: 'bolder',
                        }}
                      >
                        Max:
                      </ListItemText>
                      <ListItemText
                        style={{
                          width: '30%',
                          fontWeight: 'normal',
                        }}
                        secondaryTypographyProps={{ fontWeight: 'normal' }}
                      >
                        {formatTemp(temperature.max)}
                      </ListItemText>
                    </ListItem>
                  </List>
                </>
              )}
              {humidity && (
                <>
                  <List
                    sx={{
                      width: '100%',
                      maxWidth: 400,
                      marginLeft: isLarge ? 2 : 1,
                    }}
                    aria-labelledby="Humidity Stats"
                  >
                    <ListSubheader
                      disableGutters={true}
                      sx={{
                        fontSize: '1em',
                        color: '#4a776e',
                        fontWeight: 'bold',
                        lineHeight: '30px',
                        width: 'fit-content',
                      }}
                    >
                      Humidity
                    </ListSubheader>
                    <ListItem
                      sx={{
                        height: '24px',
                      }}
                    >
                      <ListItemText
                        style={{
                          width: isLarge ? '40%' : '20%',
                          margin: 0,
                        }}
                        primaryTypographyProps={{
                          fontSize: '1em',
                          fontWeight: 'bolder',
                        }}
                      >
                        {`${isLarge ? 'Average' : 'Avg'}:`}
                      </ListItemText>
                      <ListItemText
                        style={{
                          width: '30%',
                          fontWeight: 'normal',
                        }}
                        secondaryTypographyProps={{ fontWeight: 'normal' }}
                      >
                        {`${formatHumidity(humidity.ave)}`}
                      </ListItemText>
                      <ListItemText
                        style={{
                          width: isLarge ? '25%' : '20%',
                          margin: 0,
                        }}
                        primaryTypographyProps={{
                          fontSize: '1em',
                          fontWeight: 'bolder',
                        }}
                      >
                        Min:
                      </ListItemText>
                      <ListItemText
                        style={{
                          width: '30%',
                          fontWeight: 'normal',
                        }}
                        secondaryTypographyProps={{ fontWeight: 'normal' }}
                      >
                        {formatHumidity(humidity.min)}
                      </ListItemText>
                      <ListItemText
                        style={{
                          width: isLarge ? '25%' : '20%',
                          margin: 0,
                        }}
                        primaryTypographyProps={{
                          fontSize: '1em',
                          fontWeight: 'bolder',
                        }}
                      >
                        Max:
                      </ListItemText>
                      <ListItemText
                        style={{
                          width: '30%',
                          fontWeight: 'normal',
                        }}
                        secondaryTypographyProps={{ fontWeight: 'normal' }}
                      >
                        {formatHumidity(humidity.max)}
                      </ListItemText>
                    </ListItem>
                  </List>
                </>
              )}
            </>
          )}
        </BorderedSection>
      </CardContent>
      {/*</Collapse>*/}
    </Card>
  );
}
